import { api, apiErrorMessage, oneSpec } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    Checkbox,
    PageHeader,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { Preference } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { useOne, useWrite } from '@/lib/queries';

/** Categories whose key does not read as words (BM-43: the daily work digest). */
const CATEGORY_LABELS: Record<string, { label: string; description: string }> =
    {
        'staff.work_digest': {
            label: 'Daily work digest',
            description:
                'Your open and overdue work items and new cases, once a day in your time zone (07:00 unless you pick an hour). Sent only when there is something.',
        },
    };

/** The reads of `/settings/notifications` (BM-46 §2.0). */
export const preferencesReads = () => ({
    preferences: oneSpec('/staff/notifications/preferences'),
});

export function NotificationPreferencesPage() {
    const query = useOne<Preference[]>(preferencesReads().preferences.path);
    const [edits, setEdits] = useState<Record<string, Preference>>({});
    const [error, setError] = useState<string | null>(null);
    const rows: Preference[] = (query.data?.data ?? []).map(
        (p) => edits[p.category] ?? p,
    );
    const setRows = (update: (current: Preference[]) => Preference[]) =>
        setEdits((e) =>
            Object.fromEntries(
                update(rows.map((p) => e[p.category] ?? p)).map((p) => [
                    p.category,
                    p,
                ]),
            ),
        );
    const save = useWrite(
        async (preferences: Preference[]) =>
            api().put<Preference[]>('/staff/notifications/preferences', {
                preferences: preferences.map((p) => ({
                    category: p.category,
                    channels: p.channels,
                    digest_enabled: p.digest_enabled,
                    // BM-66: the local time of the daily digest, null for the tenant's default hour
                    daily_digest_at: p.daily_digest_at ?? null,
                })),
            }),
        ['/staff/notifications/preferences'],
    );
    const toggle = (category: string, channel: string, on: boolean) =>
        setRows((r) =>
            r.map((p) =>
                p.category === category
                    ? {
                          ...p,
                          channels: on
                              ? [...new Set([...p.channels, channel])]
                              : p.channels.filter((c) => c !== channel),
                      }
                    : p,
            ),
        );
    return (
        <div>
            <PageHeader
                title="Notification preferences"
                meta="Per category: email, the bell, or a daily digest at 08:00. Security and approvals are always on."
            />
            <DetailBody query={query} title="Preferences">
                {() => (
                    <Card>
                        <CardContent className="pt-6">
                            {error ? (
                                <p
                                    role="alert"
                                    className="text-destructive mb-3 text-sm"
                                >
                                    {error}
                                </p>
                            ) : null}
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Bell</TableHead>
                                        <TableHead>Daily digest</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map((p) => (
                                        <TableRow key={p.category}>
                                            <TableCell>
                                                <span
                                                    className={
                                                        CATEGORY_LABELS[
                                                            p.category
                                                        ]
                                                            ? ''
                                                            : 'capitalize'
                                                    }
                                                >
                                                    {CATEGORY_LABELS[p.category]
                                                        ?.label ?? p.category}
                                                </span>
                                                {CATEGORY_LABELS[p.category] ? (
                                                    <span className="text-muted-foreground block text-xs">
                                                        {
                                                            CATEGORY_LABELS[
                                                                p.category
                                                            ].description
                                                        }
                                                    </span>
                                                ) : null}
                                                {p.mandatory ? (
                                                    <span className="text-muted-foreground ml-1 text-xs">
                                                        (always on)
                                                    </span>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    aria-label={`${p.category} email`}
                                                    checked={
                                                        p.mandatory ||
                                                        p.channels.includes(
                                                            'email',
                                                        )
                                                    }
                                                    disabled={p.mandatory}
                                                    onCheckedChange={(v) =>
                                                        toggle(
                                                            p.category,
                                                            'email',
                                                            v === true,
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    aria-label={`${p.category} in-app`}
                                                    checked={
                                                        p.mandatory ||
                                                        p.channels.includes(
                                                            'in_app',
                                                        )
                                                    }
                                                    disabled={p.mandatory}
                                                    onCheckedChange={(v) =>
                                                        toggle(
                                                            p.category,
                                                            'in_app',
                                                            v === true,
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <span className="flex flex-wrap items-center gap-2">
                                                    <Switch
                                                        aria-label={`${p.category} digest`}
                                                        checked={
                                                            p.digest_enabled
                                                        }
                                                        onCheckedChange={(v) =>
                                                            setRows((r) =>
                                                                r.map((x) =>
                                                                    x.category ===
                                                                    p.category
                                                                        ? {
                                                                              ...x,
                                                                              digest_enabled:
                                                                                  v,
                                                                          }
                                                                        : x,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                    {p.category ===
                                                    'staff.work_digest' ? (
                                                        // BM-66 (design "Daily work digest at 07:00"): the hour it goes out, in your zone
                                                        <label className="text-fg-2 flex items-center gap-1 text-xs">
                                                            at
                                                            <input
                                                                type="time"
                                                                step={3600}
                                                                aria-label="Daily work digest time"
                                                                className="border-border-2 text-fg-1 h-8 rounded-md border bg-transparent px-2 text-sm"
                                                                value={
                                                                    p.daily_digest_at ??
                                                                    '07:00'
                                                                }
                                                                onChange={(e) =>
                                                                    setRows(
                                                                        (r) =>
                                                                            r.map(
                                                                                (
                                                                                    x,
                                                                                ) =>
                                                                                    x.category ===
                                                                                    p.category
                                                                                        ? {
                                                                                              ...x,
                                                                                              daily_digest_at:
                                                                                                  e
                                                                                                      .target
                                                                                                      .value ===
                                                                                                      '' ||
                                                                                                  e
                                                                                                      .target
                                                                                                      .value ===
                                                                                                      '07:00'
                                                                                                      ? null
                                                                                                      : e.target.value.slice(
                                                                                                            0,
                                                                                                            5,
                                                                                                        ),
                                                                                          }
                                                                                        : x,
                                                                            ),
                                                                    )
                                                                }
                                                            />
                                                        </label>
                                                    ) : null}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <Button
                                className="mt-4"
                                onClick={() => {
                                    setError(null);
                                    save.mutate(rows, {
                                        onError: (e) =>
                                            setError(apiErrorMessage(e)),
                                    });
                                }}
                                disabled={save.isPending}
                            >
                                Save preferences
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </DetailBody>
        </div>
    );
}
